<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!-- Progress Indicator -->
    <report-progress :showProgress="isLoading" />

    <!-- IVR Flowchart Container -->
    <div 
      ref="containerRef" 
      class="flex-1 overflow-hidden bg-white relative"
      @mousedown="startPan"
      @mousemove="onPan"
      @mouseup="endPan"
      @mouseleave="endPan"
      @wheel="onWheel"
      :style="{ cursor: isPanning ? 'grabbing' : 'grab' }"
    >
      <svg
        v-if="hasData"
        ref="svgRef"
        :width="containerWidth"
        :height="containerHeight"
        class="ivr-svg"
      >
        <g :transform="transformString">
          <!-- Edges (drawn first so nodes appear on top) -->
          <g class="edges">
            <path
              v-for="edge in edges"
              :key="edge.id"
              :d="edge.path"
              fill="none"
              stroke="#d1d5db"
              stroke-width="2"
              class="edge-path"
            />
          </g>
          
          <!-- Nodes -->
          <g class="nodes">
            <g
              v-for="node in nodes"
              :key="node.id"
              :transform="`translate(${node.x - node.width / 2}, ${node.y - nodeHeight / 2})`"
              class="node-group"
            >
              <!-- Node background -->
              <rect
                :width="node.width"
                :height="nodeHeight"
                rx="8"
                ry="8"
                fill="white"
                :stroke="getNodeColor()"
                stroke-width="2"
                class="node-rect"
              />
              
              <!-- Count text -->
              <text
                :x="node.width / 2"
                :y="nodeHeight / 2 - 8"
                text-anchor="middle"
                :fill="getNodeColor()"
                font-size="18"
                font-weight="bold"
              >
                {{ formatNumber(node.count) }}
              </text>
              
              <!-- Name text -->
              <text
                :x="node.width / 2"
                :y="nodeHeight / 2 + 14"
                text-anchor="middle"
                :fill="getNodeColor()"
                font-size="11"
                font-weight="600"
              >
                {{ node.name }}
              </text>

              <!-- Export button -->
              <g 
                class="export-btn cursor-pointer"
                :transform="`translate(${node.width - 24}, 6)`"
                @click.stop="handleNodeExport(node)"
              >
                <rect
                  width="18"
                  height="18"
                  rx="4"
                  ry="4"
                  fill="#f3f4f6"
                  class="export-btn-bg"
                />
                <!-- Download/Export icon -->
                <path
                  d="M9 3v6m0 0l-2.5-2.5M9 9l2.5-2.5M4 11v2a2 2 0 002 2h6a2 2 0 002-2v-2"
                  fill="none"
                  stroke="#6b7280"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  transform="translate(0, 0)"
                  class="export-icon"
                />
              </g>
            </g>
          </g>
        </g>
      </svg>

      <!-- Zoom Controls -->
      <div v-if="hasData" class="absolute bottom-4 right-4 flex flex-col gap-2 bg-white rounded-lg shadow-md p-2">
        <button 
          @click="zoomIn" 
          class="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-gray-700 font-bold"
        >+</button>
        <button 
          @click="zoomOut" 
          class="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-gray-700 font-bold"
        >−</button>
        <button 
          @click="resetView" 
          class="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-gray-700 text-xs"
        >⟲</button>
      </div>

      <!-- Empty State -->
      <div v-if="!hasData" class="flex items-center justify-center h-full">
        <p class="text-gray-500">{{ $t("reports.ivr_report.no_data") }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import dagre from "@dagrejs/dagre";
import ReportProgress from "../../components/ReportProgress.vue";

export default {
  name: "ivr-report",
  components: {
    ReportProgress,
  },
  props: {
    selectedFilters: {
      type: Object,
      default: () => ({}),
    },
    reportData: {
      type: [Object, Array],
      default: () => ({}),
    },
    totalCount: {
      type: Number,
      default: 0,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['exportNode'],
  data() {
    return {
      minNodeWidth: 120,
      maxNodeWidth: 280,
      nodeHeight: 70,
      horizontalSpacing: 40,
      verticalSpacing: 80,
      padding: 40,
      containerWidth: 1200,
      containerHeight: 800,
      // Pan and zoom state
      panX: 0,
      panY: 0,
      scale: 1,
      isPanning: false,
      lastMouseX: 0,
      lastMouseY: 0,
      initialized: false,
      // Unified node color
      nodeColor: "#0891b2", // cyan-600
    };
  },
  mounted() {
    this.updateContainerSize();
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
  },
  watch: {
    nodes: {
      handler() {
        // Center the graph when nodes change (first render or data update)
        if (this.nodes.length > 0 && !this.initialized) {
          this.$nextTick(() => {
            this.centerGraph();
            this.initialized = true;
          });
        }
      },
      immediate: true,
    },
    reportData: {
      handler() {
        // Reset initialization when data changes
        this.initialized = false;
      },
    },
  },
  computed: {
    hasData() {
      if (!this.reportData) return false;
      if (Array.isArray(this.reportData)) return this.reportData.length > 0;
      return Object.keys(this.reportData).length > 0;
    },
    graphData() {
      if (!this.hasData) return { nodes: [], edges: [] };

      // Handle multiple root nodes (array) or single root node
      if (Array.isArray(this.reportData)) {
        return this.buildMultipleGraphs(this.reportData);
      } else {
        return this.buildGraph(this.reportData);
      }
    },
    nodes() {
      return this.graphData.nodes;
    },
    edges() {
      return this.graphData.edges;
    },
    graphBounds() {
      if (this.nodes.length === 0) {
        return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
      }
      
      const minX = Math.min(...this.nodes.map((n) => n.x - (n.width || this.minNodeWidth) / 2));
      const maxX = Math.max(...this.nodes.map((n) => n.x + (n.width || this.minNodeWidth) / 2));
      const minY = Math.min(...this.nodes.map((n) => n.y - this.nodeHeight / 2));
      const maxY = Math.max(...this.nodes.map((n) => n.y + this.nodeHeight / 2));
      
      return {
        minX,
        maxX,
        minY,
        maxY,
        width: maxX - minX,
        height: maxY - minY,
        centerX: (minX + maxX) / 2,
        centerY: (minY + maxY) / 2,
      };
    },
    transformString() {
      return `translate(${this.panX}, ${this.panY}) scale(${this.scale})`;
    },
  },
  methods: {
    updateContainerSize() {
      this.$nextTick(() => {
        if (this.$refs.containerRef) {
          this.containerWidth = this.$refs.containerRef.clientWidth || 1200;
          this.containerHeight = this.$refs.containerRef.clientHeight || 800;
        }
      });
    },
    handleResize() {
      this.updateContainerSize();
      this.centerGraph();
    },
    centerGraph() {
      if (this.nodes.length === 0) return;
      
      const bounds = this.graphBounds;
      
      // Calculate the scale to fit the graph in the viewport with some padding
      const viewPadding = 60;
      const availableWidth = this.containerWidth - viewPadding * 2;
      const availableHeight = this.containerHeight - viewPadding * 2;
      
      const scaleX = bounds.width > 0 ? availableWidth / bounds.width : 1;
      const scaleY = bounds.height > 0 ? availableHeight / bounds.height : 1;
      
      // Use the smaller scale to fit everything, but cap at 1 (don't zoom in)
      this.scale = Math.min(scaleX, scaleY, 1);
      
      // Center the graph
      const scaledWidth = bounds.width * this.scale;
      const scaledHeight = bounds.height * this.scale;
      
      this.panX = (this.containerWidth - scaledWidth) / 2 - bounds.minX * this.scale;
      this.panY = (this.containerHeight - scaledHeight) / 2 - bounds.minY * this.scale;
    },
    startPan(event) {
      if (event.button !== 0) return; // Only left mouse button
      this.isPanning = true;
      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
    },
    onPan(event) {
      if (!this.isPanning) return;
      
      const deltaX = event.clientX - this.lastMouseX;
      const deltaY = event.clientY - this.lastMouseY;
      
      this.panX += deltaX;
      this.panY += deltaY;
      
      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
    },
    endPan() {
      this.isPanning = false;
    },
    onWheel(event) {
      event.preventDefault();
      
      const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1;
      const newScale = Math.max(0.1, Math.min(3, this.scale * zoomFactor));
      
      // Get mouse position relative to container
      const rect = this.$refs.containerRef.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      
      // Adjust pan to zoom towards mouse position
      const scaleDiff = newScale - this.scale;
      this.panX -= (mouseX - this.panX) * (scaleDiff / this.scale);
      this.panY -= (mouseY - this.panY) * (scaleDiff / this.scale);
      
      this.scale = newScale;
    },
    zoomIn() {
      const newScale = Math.min(3, this.scale * 1.2);
      // Zoom towards center
      const centerX = this.containerWidth / 2;
      const centerY = this.containerHeight / 2;
      const scaleDiff = newScale - this.scale;
      this.panX -= (centerX - this.panX) * (scaleDiff / this.scale);
      this.panY -= (centerY - this.panY) * (scaleDiff / this.scale);
      this.scale = newScale;
    },
    zoomOut() {
      const newScale = Math.max(0.1, this.scale / 1.2);
      // Zoom towards center
      const centerX = this.containerWidth / 2;
      const centerY = this.containerHeight / 2;
      const scaleDiff = newScale - this.scale;
      this.panX -= (centerX - this.panX) * (scaleDiff / this.scale);
      this.panY -= (centerY - this.panY) * (scaleDiff / this.scale);
      this.scale = newScale;
    },
    resetView() {
      this.centerGraph();
    },
    normalizeNode(node) {
      return {
        id: node.a_id || node.id,
        name: node.a_name || node.name || "",
        count: node.count || 0,
        children: node.children || [],
      };
    },
    buildGraph(rootNode, offsetX = 0, offsetY = 0) {
      const g = new dagre.graphlib.Graph();

      // Set graph options
      g.setGraph({
        rankdir: "TB", // Top to bottom
        nodesep: this.horizontalSpacing,
        ranksep: this.verticalSpacing,
        marginx: this.padding,
        marginy: this.padding,
      });

      g.setDefaultEdgeLabel(() => ({}));

      const nodes = [];
      const edges = [];
      const nodeMap = new Map();

      // Recursively add nodes and edges
      const addNode = (node, parentId = null, level = 0) => {
        const normalized = this.normalizeNode(node);
        const nodeId = `node-${normalized.id}`;

        if (nodeMap.has(nodeId)) return nodeId;

        nodeMap.set(nodeId, true);

        // Calculate dynamic width based on node name
        const nodeWidth = this.calculateNodeWidth(normalized.name);

        // Add to dagre graph with dynamic width
        g.setNode(nodeId, {
          width: nodeWidth,
          height: this.nodeHeight,
          ...normalized,
          level,
          nodeWidth, // Store the calculated width
        });

        // Add edge from parent
        if (parentId) {
          g.setEdge(parentId, nodeId);
        }

        // Process children
        if (normalized.children && normalized.children.length > 0) {
          normalized.children.forEach((child) => {
            addNode(child, nodeId, level + 1);
          });
        }

        return nodeId;
      };

      const rootNodeId = addNode(rootNode);

      // Run dagre layout
      dagre.layout(g);

      // Get root node position for alignment
      const rootNodeData = g.node(rootNodeId);
      const rootX = rootNodeData ? rootNodeData.x : 0;

      // Extract positioned nodes (shift so root is at x=0 for alignment)
      g.nodes().forEach((nodeId) => {
        const nodeData = g.node(nodeId);
        nodes.push({
          id: nodeId,
          x: nodeData.x - rootX + offsetX, // Shift so root is at offsetX
          y: nodeData.y + offsetY,
          name: nodeData.name,
          count: nodeData.count,
          level: nodeData.level,
          width: nodeData.nodeWidth, // Use the calculated width
        });
      });

      // Extract edges with paths (also shifted)
      g.edges().forEach((e) => {
        const sourceNode = g.node(e.v);
        const targetNode = g.node(e.w);

        // Create smooth bezier path
        const path = this.createEdgePath(
          sourceNode.x - rootX + offsetX,
          sourceNode.y + offsetY + this.nodeHeight / 2,
          targetNode.x - rootX + offsetX,
          targetNode.y + offsetY - this.nodeHeight / 2
        );

        edges.push({
          id: `edge-${e.v}-${e.w}`,
          source: e.v,
          target: e.w,
          path,
        });
      });

      // Calculate graph dimensions
      const graphInfo = g.graph();
      const graphHeight = graphInfo.height || 0;

      return { nodes, edges, height: graphHeight + this.padding * 2 };
    },
    buildMultipleGraphs(rootNodes) {
      const allNodes = [];
      const allEdges = [];
      let currentY = 0;
      const verticalGap = 60;

      // Build each tree with roots aligned at x=0
      rootNodes.forEach((rootNode) => {
        const { nodes, edges, height } = this.buildGraph(rootNode, 0, currentY);
        allNodes.push(...nodes);
        allEdges.push(...edges);
        currentY += height + verticalGap;
      });

      return { nodes: allNodes, edges: allEdges };
    },
    createEdgePath(x1, y1, x2, y2) {
      // Create smooth bezier curve
      const midY = (y1 + y2) / 2;
      return `M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`;
    },
    getNodeColor() {
      return this.nodeColor;
    },
    calculateNodeWidth(name) {
      // Approximate width calculation: ~7px per character for 11px font, plus padding
      const charWidth = 7;
      const horizontalPadding = 24;
      const textWidth = (name || "").length * charWidth + horizontalPadding;
      return Math.max(this.minNodeWidth, Math.min(this.maxNodeWidth, textWidth));
    },
    formatNumber(num) {
      if (num == null || num === undefined || isNaN(num)) {
        return "0";
      }
      const numValue = parseInt(num) || 0;
      if (numValue >= 1000) {
        return (numValue / 1000).toFixed(1) + "K";
      }
      return numValue.toString();
    },
    getNodeOriginalId(node) {
      // Extract the original id from the node id (remove "node-" prefix)
      const nodeId = node.id || "";
      return nodeId.replace(/^node-/, "");
    },
    handleNodeExport(node) {
      const originalId = this.getNodeOriginalId(node);
      this.$emit("exportNode", {
        isexport: true,
        node: originalId,
        name: node.name,
        count: node.count,
      });
    },
  },
};
</script>

<style scoped>
.ivr-svg {
  display: block;
  user-select: none;
}

.node-group {
  transition: filter 0.2s ease;
}

.node-group:hover .node-rect {
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
}

.node-rect {
  transition: filter 0.2s ease;
}

.edge-path {
  pointer-events: none;
}

.export-btn {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.node-group:hover .export-btn {
  opacity: 1;
}

.export-btn:hover .export-btn-bg {
  fill: #e5e7eb;
}

.export-btn:hover .export-icon {
  stroke: #374151;
}
</style>
